import React from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, ArrowUpRight, FlaskConical, MessageCircle, ShieldCheck, Users } from "lucide-react";
import "./TreatmentStory.css";

const researchSteps = [
  { title: "Review existing research", copy: "Read what has been tried and identify gaps in the evidence.", Icon: MessageCircle },
  { title: "Choose an experiment", copy: "Work with scientists who study the condition to agree on a test.", Icon: Users },
  { title: "Compare the results", copy: "Check the controls and decide whether the findings justify further work.", Icon: FlaskConical },
  { title: "Review the clinical path", copy: "Assess quality, consent, and the oversight required.", Icon: ShieldCheck },
  { title: "Follow the evidence", copy: "Where studies proceed, monitor safety and outcomes.", Icon: Activity },
];

const patientPages = [
  { id: "overview", href: "/patients", label: "Why we’re building this" },
  { id: "research", href: "/patients/research", label: "How research could work" },
  { id: "questions", href: "/patients/questions", label: "Questions about research and care" },
];

function ResearchConversation() {
  return (
    <figure className="treatment-conversation" aria-label="An illustrative future research conversation, not a patient testimonial">
      <figcaption>A future research conversation · Illustrated</figcaption>
      <div className="treatment-question">
        <MessageCircle size={18} aria-hidden="true" />
        <p>Where would you start looking for a new treatment?</p>
      </div>
      <div className="treatment-team" aria-hidden="true">
        <img src="/images/discovery/principal-scientist.png" alt="" width="80" height="80" loading="lazy" />
        <img src="/images/discovery/computational-scientist.png" alt="" width="80" height="80" loading="lazy" />
        <img src="/images/discovery/researcher.png" alt="" width="80" height="80" loading="lazy" />
      </div>
      <div className="treatment-response">
        <p>We’d start with what researchers have already tried, and where they got stuck.</p>
      </div>
    </figure>
  );
}

function PatientOverview() {
  return (
    <>
      <section className="treatment-opening journey-shell" aria-labelledby="treatment-title">
        <div className="treatment-introduction">
          <h1 className="journey-title" id="treatment-title">Every life is worth{" "}<em>developing a medicine for.</em></h1>
          <p className="journey-copy">We’re building Cornucopia to help scientists test ideas that could become new treatments.</p>
        </div>
        <ResearchConversation />
      </section>
    </>
  );
}

function PatientResearch() {
  return (
    <>
      <section className="treatment-research-intro journey-shell" aria-labelledby="treatment-research-title">
        <h1 className="journey-title" id="treatment-research-title">What a research<br /><em>project would involve.</em></h1>
        <p className="journey-copy">A proposed project would start with a review of the existing research. Scientists would then decide what to test and how to judge the results.</p>
      </section>
      <section className="treatment-path-section" aria-label="Five stages of the proposed research process">
        <div className="journey-shell">
          <ol className="treatment-path">
            {researchSteps.map(({ title, copy, Icon }, index) => (
              <li key={title}>
                <div className="treatment-step-track">
                  <span className="treatment-step-icon"><Icon size={27} strokeWidth={1.5} aria-hidden="true" /></span>
                  {index < researchSteps.length - 1 && <ArrowRight className="treatment-step-arrow" size={18} aria-hidden="true" />}
                </div>
                <span className="treatment-step-number">0{index + 1}</span>
                <h2>{title}</h2>
                <p>{copy}</p>
              </li>
            ))}
          </ol>
          <p className="treatment-path-caption">A project can change direction or stop when the evidence does not support going further.</p>
        </div>
      </section>
      <div className="treatment-research-care journey-shell">
        <ShieldCheck size={30} strokeWidth={1.4} aria-hidden="true" />
        <p>Before any human use, a proposed treatment needs a suitable legal pathway, appropriate manufacturing, and clinical oversight. A promising laboratory result alone cannot establish that a medicine will help someone.</p>
      </div>
    </>
  );
}

function SourceLink({ href, children }) {
  return <a href={href} target="_blank" rel="noreferrer">{children} <ArrowUpRight size={14} aria-hidden="true" /></a>;
}

function PatientQuestions() {
  return (
    <section className="treatment-safeguards journey-shell" aria-labelledby="treatment-safeguards-title">
      <div className="treatment-safeguards-heading">
        <h1 className="journey-title" id="treatment-safeguards-title">Questions about{" "}<br /><em>research and care.</em></h1>
        <p>These answers describe the U.S. framework. Your clinician can help you understand what applies to your care.</p>
      </div>
      <div className="treatment-disclosures">
        <details>
          <summary>Does Cornucopia offer treatment?<span aria-hidden="true">+</span></summary>
          <div><p>Our invitation today is to discuss research questions. We do not offer medical care or access to an experimental medicine. Making drug discovery more accessible is the future we are working toward.</p></div>
        </details>
        <details>
          <summary>Who decides about treatment?<span aria-hidden="true">+</span></summary>
          <div>
            <p>Qualified clinicians make clinical decisions with the patient. Human investigation under an investigational new drug application (IND) involves FDA review, informed consent, and independent ethics review. The exact requirements depend on the pathway.</p>
            <SourceLink href="https://www.fda.gov/drugs/types-applications/investigational-new-drug-application-ind">FDA: clinical investigation</SourceLink>
          </div>
        </details>
        <details>
          <summary>What has to happen before human use?<span aria-hidden="true">+</span></summary>
          <div>
            <p>Researchers must assess potential harm before testing a new drug in people. Material for human use also needs suitable manufacturing and quality controls. A cloud-lab experiment alone does not establish these.</p>
            <SourceLink href="https://www.fda.gov/patients/drug-development-process/step-2-preclinical-research">FDA: preclinical research</SourceLink>
            <SourceLink href="https://www.fda.gov/regulatory-information/search-fda-guidance-documents/current-good-manufacturing-practice-phase-1-investigational-drugs">FDA: manufacturing quality</SourceLink>
          </div>
        </details>
        <details>
          <summary>How do clinical trials and expanded access fit in?<span aria-hidden="true">+</span></summary>
          <div>
            <p>Clinical trials study an investigational treatment’s safety and effects. In specific circumstances, expanded access may allow treatment outside a trial. A clinician can discuss these options; eligibility, a willing supplier, and regulatory safeguards still apply.</p>
            <SourceLink href="https://www.fda.gov/patients/drug-development-process/step-3-clinical-research">FDA: clinical trials</SourceLink>
            <SourceLink href="https://www.fda.gov/news-events/expanded-access/expanded-access-information-patients">FDA: expanded access</SourceLink>
          </div>
        </details>
        <details>
          <summary>What about risk, time, and cost?<span aria-hidden="true">+</span></summary>
          <div>
            <p>A candidate may not work, may cause serious harm, or may never reach clinical investigation. Development can take substantial time and funding. Insurance may not cover investigational treatment or related care, and access is never guaranteed.</p>
            <SourceLink href="https://www.fda.gov/news-events/expanded-access/expanded-access-information-patients">FDA: risks and costs</SourceLink>
          </div>
        </details>
        <details>
          <summary>What should I share in an inquiry?<span aria-hidden="true">+</span></summary>
          <div>
            <p>A general, nonconfidential research question is enough to start. Please leave out medical records, genetic files, and personal health details. Any future work with sensitive information would need appropriate privacy protections and permissions first.</p>
            <SourceLink href="https://www.hhs.gov/hipaa/for-professionals/special-topics/research/index.html">HHS: health information and research</SourceLink>
          </div>
        </details>
      </div>
    </section>
  );
}

function PatientNextPages({ page }) {
  return (
    <nav className="treatment-next-pages journey-shell" aria-label="Explore the patient pages">
      {patientPages.filter(({ id }) => id !== page).map(({ href, label }) => (
        <Link to={href} key={href}><span>{label}</span><ArrowRight size={20} aria-hidden="true" /></Link>
      ))}
    </nav>
  );
}

export default function TreatmentStory({ page = "overview" }) {
  const currentPage = patientPages.some(({ id }) => id === page) ? page : "overview";
  return (
    <div className="treatment-story" data-page={currentPage}>
      {currentPage === "overview" && <PatientOverview />}
      {currentPage === "research" && <PatientResearch />}
      {currentPage === "questions" && <PatientQuestions />}
      <PatientNextPages page={currentPage} />
      <section className="treatment-contact journey-shell" aria-label="Start a research conversation">
        <Link className="journey-button" to="/patients/contact?source=treatment-story">Talk with us <ArrowUpRight size={18} aria-hidden="true" /></Link>
        <p className="treatment-contact-note">You can speak with our team about research. We don’t provide medical care or access to treatment. Please keep your inquiry nonconfidential and leave out medical records or personal health details.</p>
      </section>
    </div>
  );
}
